import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../i18n";
import { ApiError, getWishesFeed, likeWish, type FeedPost, type WishSort } from "../lib/api";
import { hasLiked, markLiked } from "../lib/likes";
import { ArrowLeft, Heart } from "../components/Icon";

const PAGE = 20;

export function Wishes() {
  const { t } = useI18n();
  const [sort, setSort] = useState<WishSort>("latest");
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(false); // prevents overlapping page loads
  const reqIdRef = useRef(0); // invalidates in-flight loads after a sort reset

  // Reset the feed (and cancel any in-flight load) whenever the sort mode changes.
  useEffect(() => {
    reqIdRef.current++;
    loadingRef.current = false;
    setPosts([]);
    setCursor(null);
    setDone(false);
    setLoading(false);
  }, [sort]);

  const loadMore = useCallback(async () => {
    if (loadingRef.current || done) return;
    loadingRef.current = true;
    setLoading(true);
    const myReq = reqIdRef.current;
    try {
      const r = await getWishesFeed(sort, cursor, PAGE);
      if (reqIdRef.current !== myReq) return; // superseded by a sort change — drop stale page
      setPosts((p) => [...p, ...r.posts]);
      setCursor(r.nextCursor);
      if (!r.nextCursor) setDone(true);
    } catch {
      // Stop trying on error; the sentinel will retry if the user scrolls again.
    } finally {
      if (reqIdRef.current === myReq) {
        loadingRef.current = false;
        setLoading(false);
      }
    }
  }, [sort, cursor, done]);

  // Load the first page, and reload after a sort reset, as soon as the list is empty.
  useEffect(() => {
    if (posts.length === 0 && !done) loadMore();
  }, [posts.length, done, loadMore]);

  // Infinite scroll: load subsequent pages as the sentinel approaches the viewport.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "400px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [loadMore]);

  return (
    <div className="container-x max-w-4xl py-10">
      <Link to="/wall" className="inline-flex items-center gap-1.5 text-sm text-ink/60 hover:text-[var(--theme-deep)]">
        <ArrowLeft className="h-4 w-4" /> {t("wall.title")}
      </Link>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <h1 className="section-title flex items-center gap-2">
          {t("wishes.title")} <Heart className="h-6 w-6" />
        </h1>
        <div className="inline-flex rounded-full bg-white p-1 shadow-card ring-1 ring-black/[0.06]">
          <SortBtn active={sort === "latest"} onClick={() => setSort("latest")}>
            {t("wishes.sortLatest")}
          </SortBtn>
          <SortBtn active={sort === "like"} onClick={() => setSort("like")}>
            {t("wishes.sortLiked")}
          </SortBtn>
        </div>
      </div>

      {posts.length === 0 && done ? (
        <p className="mt-8 text-center text-ink/50">{t("wishes.empty")}</p>
      ) : (
        <div className="mt-5 columns-2 gap-4 md:columns-3 [&>*]:mb-4">
          {posts.map((p) => (
            <WishCard key={p.id} post={p} />
          ))}
        </div>
      )}

      {/* Sentinel + status row */}
      <div ref={sentinelRef} className="h-10" />
      {loading && <p className="mt-2 text-center text-sm text-ink/50">{t("common.loading")}</p>}
      {done && posts.length > 0 && <p className="mt-2 text-center text-sm text-ink/40">{t("wishes.end")}</p>}
    </div>
  );
}

function SortBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${
        active ? "text-white" : "text-ink/60 hover:text-[var(--theme-deep)]"
      }`}
      style={active ? { background: "var(--theme-main)" } : undefined}
    >
      {children}
    </button>
  );
}

function WishCard({ post }: { post: FeedPost }) {
  const { t } = useI18n();
  const [likes, setLikes] = useState(post.likes);
  const [liked, setLiked] = useState(() => hasLiked(post.id));
  const [busy, setBusy] = useState(false);

  async function onLike() {
    if (liked || busy) return; // already liked on this device → no network call (saves D1)
    setBusy(true);
    setLiked(true); // optimistic
    setLikes((n) => n + 1);
    markLiked(post.id);
    try {
      const r = await likeWish(post.id);
      setLikes(r.likes); // reconcile with the server-authoritative count
    } catch (err) {
      // Roll back on failure (rate limit, network, etc.).
      setLiked(false);
      setLikes((n) => Math.max(0, n - 1));
      if (err instanceof ApiError && err.code === "rate_limited") alert(t("wishes.likeRateLimited"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="break-inside-avoid rounded-2xl bg-white/80 p-3 shadow-card ring-1 ring-black/5">
      {post.has_image && (
        <img
          src={`/api/wall/img/${post.id}`}
          alt={post.message ?? "wish"}
          loading="lazy"
          className="mb-2 w-full rounded-xl object-cover"
        />
      )}
      {post.message && <p className="whitespace-pre-wrap text-sm text-ink/80">{post.message}</p>}
      {post.display_name && (
        <p className="mt-1 text-xs font-semibold text-[var(--theme-deep)]">— {post.display_name}</p>
      )}
      <button
        type="button"
        onClick={onLike}
        disabled={liked || busy}
        aria-pressed={liked}
        className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold transition ${
          liked ? "text-[var(--theme-deep)]" : "text-ink/50 hover:text-[var(--theme-deep)]"
        }`}
      >
        <Heart className="h-4 w-4" fill={liked ? "currentColor" : "none"} />
        {likes}
      </button>
    </div>
  );
}
